import {
  Button,
  Checkbox,
  Icon,
  Modal,
  ModalHeader,
  ModalHeading,
  ModalMain,
  Select,
  Tooltip,
} from '@sixfold/common-ui';
import { notNil } from '@sixfold/typed-primitives';
import React, { useState } from 'react';
import ReactDiffViewer, { DiffMethod, ReactDiffViewerProps } from 'react-diff-viewer-continued';

import styles from './diff_modal.module.css';

interface Props {
  // Key used to save modal preferences to localStorage
  dataKey: string;
  isCompareButtonDisabled: boolean;
  showDiffViewer: boolean;
  oldValue: ReactDiffViewerProps['oldValue'] | undefined;
  newValue: ReactDiffViewerProps['newValue'] | undefined;
  leftTitle: ReactDiffViewerProps['leftTitle'] | ((useUnifiedView: boolean) => ReactDiffViewerProps['leftTitle']);
  rightTitle: ReactDiffViewerProps['rightTitle'] | ((useUnifiedView: boolean) => ReactDiffViewerProps['rightTitle']);
}

export const DiffViewerModal: React.FunctionComponent<Props> = (props: Props) => {
  const { dataKey, showDiffViewer, isCompareButtonDisabled, newValue, oldValue, leftTitle, rightTitle } = props;

  const [open, setOpen] = useState<boolean>(false);
  const [unifiedView, setUnifiedView] = useState<boolean>(localStorage.getItem(`${dataKey}.unifiedView`) === 'true');
  const [useDarkTheme, setUseDarkTheme] = useState<boolean>(localStorage.getItem(`${dataKey}.useDarkTheme`) === 'true');
  const [compareMethod, setCompareMethod] = useState<DiffMethod>(
    (localStorage.getItem(`${dataKey}.compareMethod`) as DiffMethod) ?? DiffMethod.WORDS,
  );

  const diffMethodOptions = [
    { value: DiffMethod.CHARS, label: 'Chars' },
    { value: DiffMethod.WORDS, label: 'Words' },
    { value: DiffMethod.WORDS_WITH_SPACE, label: 'Words with space' },
    { value: DiffMethod.LINES, label: 'Lines' },
    { value: DiffMethod.TRIMMED_LINES, label: 'Trimmed lines' },
    { value: DiffMethod.SENTENCES, label: 'Sentences' },
    { value: DiffMethod.CSS, label: 'CSS' },
  ];

  return (
    <Modal
      size={unifiedView ? 'large' : 'xlarge'}
      className={unifiedView ? undefined : styles.diff_modal__fullscreen}
      onToggle={(nextOpen) => setOpen(nextOpen)}
      open={open}
      trigger={<Button disabled={isCompareButtonDisabled}>Compare</Button>}>
      <ModalHeader>
        <div className={styles.diff_modal__header}>
          <ModalHeading>Message diff</ModalHeading>
          <div className={styles.diff_modal__header__options}>
            <Checkbox
              label="Unified view"
              checked={unifiedView}
              onChange={({ checked }) => {
                if (notNil(checked)) {
                  setUnifiedView(checked);
                  localStorage.setItem(`${dataKey}.unifiedView`, checked.toString());
                }
              }}
            />
            <div className={styles.diff_modal__separator}>｜</div>
            <Checkbox
              label="Dark theme"
              checked={useDarkTheme}
              onChange={({ checked }) => {
                if (notNil(checked)) {
                  setUseDarkTheme(checked);
                  localStorage.setItem(`${dataKey}.useDarkTheme`, checked.toString());
                }
              }}
            />
            <div className={styles.diff_modal__separator}>｜</div>
            <div style={{ width: '160px' }}>
              <Select
                label="Diff method"
                labelPosition="hidden"
                options={diffMethodOptions}
                value={diffMethodOptions.find((o) => o.value === compareMethod) ?? null}
                onChange={(option) => {
                  if (option) {
                    setCompareMethod(option.value as DiffMethod);
                    localStorage.setItem(`${dataKey}.compareMethod`, option.value.toString());
                  }
                }}
              />
            </div>
            <Tooltip content="Click here for info about diff methods">
              <a href={'https://github.com/kpdecker/jsdiff/tree/v4.0.1#api'} target="_blank" rel="noreferrer">
                <Icon name="help" size={12} />
              </a>
            </Tooltip>
          </div>
        </div>
      </ModalHeader>
      <ModalMain>
        <div className={styles.diff_modal}>
          {showDiffViewer && (
            <ReactDiffViewer
              leftTitle={typeof leftTitle === 'function' ? leftTitle(unifiedView) : leftTitle}
              rightTitle={typeof rightTitle === 'function' ? rightTitle(unifiedView) : rightTitle}
              oldValue={oldValue}
              newValue={newValue}
              splitView={!unifiedView}
              useDarkTheme={useDarkTheme}
              compareMethod={compareMethod}
            />
          )}
        </div>
      </ModalMain>
    </Modal>
  );
};
